import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import { useFormContext } from 'react-hook-form';
import uuid from 'uuid';

import { TitleInstruction, InstructionTypes } from '../../../../types/instructions';
import { selectTitleInstruction } from '../../../../store/modules/script-editor/selectors';
import { setOpenTitleInstructionModal } from '../../../../store/modules/script-editor/actions';
import { parseJsonToEditorStateWithInstruction } from '../../../../services/editorJS/utils';
import {
  emitInstructionEventByEditor,
  removeInstructionElement,
  setInstructionActionTooltip,
} from '../utils';
import { InstructionActionTooltip, InstructionEvent } from '../types';
import {
  ModalComponent,
  InputComponent,
  SelectComponent,
} from '../components';
import { Presenters } from '../../../../configs/instructionConfig';
import DuplicateBlock from '../../../../services/editorJS/DuplicateBlock';
import { notify } from '../../../../store/modules/notifications/actions';

type Props = {
  instanceEditor: EditorJS | undefined;
};

const scriptFieldName = 'script';
const defaultState = {
  comment: '',
  presenter: 0,
};

const TitleInstructionModal = ({ instanceEditor }: Props) => {
  const dispatch = useDispatch();
  const { getValues, setValue } = useFormContext();
  const { isOpenModal, instructionId, isInline } = useSelector(selectTitleInstruction);
  const [comment, setComment] = useState(defaultState.comment);
  const [presenter, setPresenter] = useState(defaultState.presenter);
  const [initialState, setInitialState] = useState(defaultState);

  useEffect(() => {
    (async () => {
      if (instructionId) {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instruction = scriptData.instructions.find(
          (item) => item.id === instructionId && item.type === InstructionTypes.titleInstruction,
        );
        if (instruction) {
          const { data } = instruction as TitleInstruction;
          setComment(instruction.comment);
          setPresenter(data.presenter);
          setInitialState({
            comment: instruction.comment,
            presenter: data.presenter,
          });
        } else {
          setComment(defaultState.comment);
          setPresenter(defaultState.presenter);
          setInitialState(defaultState);
        }
      } else {
        setComment(defaultState.comment);
        setPresenter(defaultState.presenter);
        setInitialState(defaultState);
      }
    })();
  }, [
    setComment,
    setPresenter,
    instructionId,
    getValues,
    setInitialState,
  ]);

  const closeHandler = useCallback(() => {
    if (instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.SET_TITLE_INSTRUCTION_SELECTION,
        {},
      );
    }
    dispatch(setOpenTitleInstructionModal(false));
  }, [dispatch, instanceEditor]);

  const getSummary = useCallback(() => {
    const presenterItem = Presenters.find((item) => item.value === presenter);
    if (presenterItem) {
      return presenterItem.title;
    }
    if (comment.trim()) {
      return comment;
    }
    return 'Title';
  }, [comment, presenter]);

  const saveHandler = useCallback(() => {
    (async () => {
      if (instanceEditor) {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instructionIndex = scriptData.instructions.findIndex(
          (item) => item.id === instructionId && item.type === InstructionTypes.titleInstruction,
        );
        const titleInstruction: TitleInstruction = {
          id: instructionId,
          type: InstructionTypes.titleInstruction,
          comment,
          needsInfo: false,
          data: {
            presenter,
          },
        };
        const summary = getSummary();
        if (instructionIndex !== -1) {
          scriptData.instructions[instructionIndex] = titleInstruction;
          if (instructionIndex !== -1 && isInline) {
            setInstructionActionTooltip(
              {
                instructionId,
                tooltip: `${InstructionActionTooltip.title}: ${summary}`,
                scriptBlocks: scriptData.blocks,
              },
            );
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.UPDATE_TITLE_ACTION_EVENT_LISTENERS,
              {
                instanceEditor,
                refEditor: window.scriptEditorRef!,
              },
            );
          }
          instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
        } else {
          const id = (instructionId.trim()) ? instructionId : uuid();
          titleInstruction.id = id;
          scriptData.instructions.push(titleInstruction);
          if (isInline) {
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.SAVE_TITLE_INSTRUCTION,
              { instructionId: id, tooltip: summary },
            );
          }
        }
        if (!isInline) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.SAVE_TITLE_INSTRUCTION_BLOCK,
            { instructionId: titleInstruction.id, text: summary, tooltip: summary },
          );
        }
        setValue(scriptFieldName, JSON.stringify(scriptData));
        closeHandler();
      }
    })();
  }, [
    instanceEditor,
    getValues,
    setValue,
    instructionId,
    closeHandler,
    comment,
    presenter,
    isInline,
    getSummary,
  ]);

  const cancelHandler = useCallback(() => {
    (async () => {
      setComment(initialState.comment);
      setPresenter(initialState.presenter);
      const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
      const isInstructionCreated = scriptData.instructions.some(
        (item) => item.id === instructionId,
      ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
      if (instanceEditor) {
        if (!isInstructionCreated) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
            {},
          );
        }
        emitInstructionEventByEditor(
          instanceEditor,
          InstructionEvent.CANCEL_INSTRUCTION,
          {},
        );
      }
      closeHandler();
    })();
  }, [
    closeHandler,
    setComment,
    setPresenter,
    initialState,
    instanceEditor,
    instructionId,
    getValues,
  ]);

  const removeHandler = useCallback(() => {
    if (!isInline && instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
        {},
      );
    } else {
      removeInstructionElement({ instructionId, isInline })
        .then(() => {
          if (instanceEditor) {
            instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
          }
        });
    }
    closeHandler();
  }, [closeHandler, instructionId, isInline, instanceEditor]);

  const duplicate = useCallback(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues().script);
    const notifHandler = () => dispatch(
      notify({
        type: 'success',
        icon: 'copy',
        message: 'Instruction duplicated.',
      }),
    );
    if (instanceEditor) {
      const duplicateBlockService = new DuplicateBlock(instanceEditor, notifHandler, setValue, instructionId, scriptData);
      if (!isInline) {
        duplicateBlockService.duplicateBlock();
      } else {
        duplicateBlockService.duplicateInline();
      }
    }
  }, [instanceEditor, getValues, instructionId, isInline, setValue, dispatch]);

  const showKebabMenu = useMemo(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
    return scriptData.instructions.some(
      (item) => item.id === instructionId,
    ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
  }, [getValues, instructionId]);

  return (
    <ModalComponent
      open={isOpenModal}
      title={getSummary()}
      saveHandler={saveHandler}
      cancelHandler={cancelHandler}
      deleteHandler={removeHandler}
      duplicate={duplicate}
      showKebabMenu={showKebabMenu}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Comment"
            value={comment}
            onChange={setComment}
            autoFocus
          />
        </Grid>
        <Grid item xs={6}>
          <SelectComponent
            label="Presenter"
            value={presenter}
            items={Presenters}
            changeHandler={setPresenter}
            isNone={false}
          />
        </Grid>
      </Grid>
    </ModalComponent>
  );
};

export default TitleInstructionModal;
